import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Space, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { OAuthAccountOut, UserOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { OAuthProviderTag, UserRoleTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface OAuthAccountGeneralTabProps {
  record: OAuthAccountOut;
  user?: UserOut;
}

export const OAuthAccountGeneralTab = ({
  record,
  user,
}: OAuthAccountGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'oauth_name',
      label: t('iam.oauth_accounts.fields.oauth_name'),
      children: <OAuthProviderTag provider={record.oauth_name} />,
    },
    {
      key: 'account_email',
      label: t('iam.oauth_accounts.fields.account_email'),
      children: orDash(
        record.account_email ? (
          <Text copyable>{record.account_email}</Text>
        ) : null
      ),
    },
    {
      key: 'account_id',
      label: t('iam.oauth_accounts.fields.account_id'),
      children: orDash(
        record.account_id ? (
          <Text code copyable>
            {record.account_id}
          </Text>
        ) : null
      ),
    },
    {
      key: 'user',
      label: t('iam.oauth_accounts.fields.user'),
      children: (
        <Space>
          <ShowIconLink resource="iam.users" id={record.user_id}>
            {user?.email ?? user?.username ?? record.user_id}
          </ShowIconLink>
          {user && <UserRoleTag role={user.role} />}
        </Space>
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};
